import React from "react";
import { Link } from "react-router-dom";
import { Quote } from "lucide-react";
import Navbar from "../components/site/Navbar";
import Footer from "../components/site/Footer";
import { CtaBand } from "../components/site/CtaBand";
import { Reveal } from "../lib/motion";
import { useSEO, useLenis } from "../lib/hooks";

const VALUES = [
  { k: "Clarity over complexity", v: "Plain answers a store owner can act on — not dashboards nobody opens." },
  { k: "Built for Indian retail", v: "Grounded in how fashion stores here actually buy, price and sell." },
  { k: "Data that actually gets used", v: "An insight only counts when it changes a real decision on the floor." },
  { k: "A friend, not a vendor", v: "We sit on your side of the table, in your language, at your pace." },
];

const About = () => {
  useLenis();
  useSEO({
    title: "About JSC — Jay Shah Consultancy, Pune Business Analytics Firm",
    description:
      "Jay Shah Consultancy is a business intelligence consultancy in Pune delivering data analytics for Indian fashion retail. Meet the ForesightFlow team.",
    keywords:
      "business intelligence Pune, data analytics consultancy India, fashion retail analytics, retail business intelligence platform, ForesightFlow team, Jay Shah Consultancy, small business analytics India, retail data insights",
  });

  return (
    <div className="jsc-grain relative bg-[#FAF8F5] min-h-screen" data-testid="page-about">
      <Navbar />

      {/* 1. HERO */}
      <section className="relative pt-24 md:pt-28 overflow-hidden">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24">
          <Reveal>
            <div className="relative rounded-[4px] overflow-hidden bg-[#1C1C1E]">
              <img
                src="/assets/jsc-wall.jpg"
                alt="JSC office wall logo — Jay Shah Consultancy"
                width="1210" height="860" loading="eager"
                className="w-full h-auto object-contain"
              />
            </div>
          </Reveal>

          <div className="mt-10 md:mt-14 max-w-4xl">
            <Reveal>
              <p className="font-body text-xs md:text-sm uppercase tracking-[0.24em] text-[#1C1C1E] mb-6 flex items-center gap-3">
                <span className="inline-block w-8 h-px bg-[#B39B77]" />
                About Jay Shah Consultancy
              </p>
            </Reveal>
            <h1 data-testid="about-headline" className="font-display font-semibold text-[#1C1C1E] text-4xl md:text-6xl lg:text-7xl leading-[0.98] tracking-tight">
              The team behind your business intelligence.
            </h1>
            <Reveal delay={0.15}>
              <p className="mt-8 max-w-2xl font-body text-lg md:text-xl leading-relaxed text-[#3E4A5C]">
                JSC is a Pune-based business intelligence consultancy and data analytics firm built
                specifically for Indian fashion retail. We help store owners read their own numbers
                and turn them into confident, everyday decisions — the thinking that became{" "}
                <Link to="/foresightflow" className="text-[#1C1C1E] link-underline font-medium">
                  the ForesightFlow platform
                </Link>
                .
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 2. FOUNDER STORY */}
      <section className="relative py-20 md:py-28 mt-6 border-t border-[rgba(28,28,30,0.08)]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            <div className="lg:col-span-5">
              <Reveal>
                <div className="relative aspect-[4/5] rounded-[3px] overflow-hidden bg-[#1C1C1E]">
                  <img
                    src="/assets/jay-shah.jpg"
                    alt="Jay Shah, Founder and CEO of Jay Shah Consultancy"
                    width="943" height="1178" loading="lazy"
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1C1C1E]/40 via-transparent to-transparent" />
                  <div className="absolute bottom-6 left-6">
                    <p className="font-body text-[10px] uppercase tracking-[0.2em] text-[#FAF8F5]/70">Founder &amp; CEO</p>
                    <p className="font-display text-2xl text-[#FAF8F5]">Jay Shah</p>
                  </div>
                </div>
              </Reveal>
            </div>

            <div className="lg:col-span-7">
              <Reveal><h2 className="font-display font-semibold text-[#1C1C1E] text-3xl md:text-5xl leading-[1.05] tracking-tight">Why we started JSC</h2></Reveal>
              <Reveal delay={0.1}>
                <div className="mt-8 space-y-6 font-body text-lg leading-relaxed text-[#3E4A5C] max-w-2xl">
                  <p>
                    Jay Shah founded Jay Shah Consultancy in January 2025 after years of watching a
                    strange gap play out across Indian fashion retail: owners were sitting on years
                    of sales, inventory and product data, yet almost none of it reached the moment a
                    real decision was made. Discounts, restocking and buying calls were still made on
                    instinct — not for lack of data, but because nobody had made that data readable.
                  </p>
                  <p>
                    That gap became the vision behind ForesightFlow: a retail business intelligence
                    platform that speaks plainly, is built for how Indian stores actually operate,
                    and puts small business analytics within reach of owners who were never going to
                    hire a full data team. The goal was never more reports — it was clarity.
                  </p>
                </div>
              </Reveal>
              <Reveal delay={0.2}>
                <blockquote className="mt-10 border-l-2 border-[#B39B77] pl-6 max-w-2xl" data-testid="founder-quote">
                  <Quote className="w-6 h-6 text-[#B39B77] mb-3" strokeWidth={1.5} />
                  <p className="font-display italic text-2xl md:text-3xl leading-snug text-[#1C1C1E]">
                    &ldquo;Most owners don&apos;t need more reports. They need someone to tell them, in
                    plain words, what their own numbers are already saying.&rdquo;
                  </p>
                  <footer className="mt-4 font-body text-sm uppercase tracking-[0.18em] text-[#3E4A5C]">Jay Shah, Founder &amp; CEO</footer>
                </blockquote>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* 3. MISSION & VALUES */}
      <section className="relative py-20 md:py-28 bg-[#F0EBE1]/40 border-y border-[rgba(28,28,30,0.06)]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <Reveal>
                <p className="font-body text-xs uppercase tracking-[0.24em] text-[#B39B77] mb-4">What we stand for</p>
                <h2 className="font-display font-semibold text-[#1C1C1E] text-3xl md:text-5xl leading-[1.05] tracking-tight">
                  A friend for your business, not a distant vendor.
                </h2>
                <p className="mt-6 font-body text-lg leading-relaxed text-[#3E4A5C] max-w-md">
                  As a data analytics consultancy in India, we measure ourselves by one thing:
                  whether our fashion retail analytics actually change what you do next. Get to know{" "}
                  <Link to="/team" className="text-[#1C1C1E] link-underline font-medium">the JSC team</Link>, or read the{" "}
                  <Link to="/blogs" className="text-[#1C1C1E] link-underline font-medium">retail insights blog</Link>.
                </p>
              </Reveal>
            </div>

            <div className="lg:col-span-7">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-[rgba(28,28,30,0.1)] border border-[rgba(28,28,30,0.1)]">
                {VALUES.map((val, i) => (
                  <Reveal key={val.k} delay={i * 0.08}>
                    <div className="bg-[#FAF8F5] p-8 h-full" data-testid={`value-${i}`}>
                      <p className="font-display text-sm text-[#B39B77]">0{i + 1}</p>
                      <h3 className="mt-3 font-display text-2xl text-[#1C1C1E]">{val.k}</h3>
                      <p className="mt-3 font-body text-base leading-relaxed text-[#3E4A5C]">{val.v}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4. CLOSING CTA */}
      <CtaBand eyebrow="Say hello" title="Want to meet the team behind ForesightFlow?" testid="about-cta" />
      <Footer />
    </div>
  );
};

export default About;
